"use client";

import { useSyncExternalStore } from "react";
import Image from "next/image";
import { X } from "lucide-react";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.customer.fastdelivery&hl=fr";
const DISMISS_KEY = "fast.androidInstallBannerDismissed";

function shouldShowBanner(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isAndroid = /android/i.test(ua);
  // Don't show when the site is already running as an installed PWA.
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true;
  const dismissed = localStorage.getItem(DISMISS_KEY) === "1";
  return isAndroid && !isStandalone && !dismissed;
}

// Lets the banner re-evaluate `shouldShowBanner` after the user dismisses it,
// without an effect that synchronously sets state on mount.
const bannerListeners = new Set<() => void>();
function subscribeBanner(cb: () => void) {
  bannerListeners.add(cb);
  return () => {
    bannerListeners.delete(cb);
  };
}

/**
 * Custom "Smart App Banner" for Android.
 *
 * iOS Safari renders a native banner from the `apple-itunes-app` meta tag (set
 * in the root layout metadata). Android has no native equivalent, so we mimic
 * the same look & behaviour here: a thin bar at the top of the page, in normal
 * document flow so it pushes content down exactly like the native iOS banner.
 * The app scrolls on the window (see ScrollArea), so a flow element here works
 * across every layout without fighting the sticky header or fixed bottom-nav.
 */
export function AppInstallBanner() {
  const show = useSyncExternalStore(subscribeBanner, shouldShowBanner, () => false);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    bannerListeners.forEach((cb) => cb());
  };

  if (!show) return null;

  return (
    <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-2 py-2 dark:border-[#313131] dark:bg-[#1E1E1E]">
      <button
        onClick={dismiss}
        aria-label="Fermer"
        className="shrink-0 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-[#2A2A2A]"
      >
        <X className="h-4 w-4" />
      </button>

      <Image
        src="/app-icon.png"
        alt="Fast"
        width={44}
        height={44}
        className="h-11 w-11 shrink-0 rounded-[10px]"
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">Fast</p>
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
          GRATUIT — Sur Google Play
        </p>
      </div>

      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 rounded-full bg-secondary px-4 py-1.5 text-sm font-bold text-white transition hover:bg-secondary/90"
      >
        Installer
      </a>
    </div>
  );
}
